import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { WASH_SPRING } from "@/components/landing/WashScenes";
import { CeramicCallout, HeroLetters, NEEDS_CAR } from "@/components/landing/HeroLetters";
import { useSiteLoaded, useSiteText } from "@/lib/siteContent";
import { mapFor, parseList, readAnchor, useBox } from "@/lib/heroLayout";
import { pickCar, useWashCars } from "@/lib/washOptions";
import { RotatePrompt } from "@/components/landing/RotatePrompt";

// Owner's effect setup for this photo: pinned frames only (no base photo), or base + enabled frames with a "first & longest" one
export const effectPlan = (site, base) => {
  const all = parseList(site("hero.effects")).filter((f) => f.base === base && f.url);
  const pins = new Set([...parseList(site("hero.effect_pins")), site("hero.effect_pin")].filter(Boolean));
  const item = (f) => ({ url: f.url, video: f.video || "" });
  const pinned = all.filter((f) => pins.has(f.url));
  if (pinned.length) return { items: pinned.map(item), slow: false, hideText: pinned.every((f) => f.hideText) };
  const on = all.filter((f) => f.on !== false);
  const first = on.find((f) => f.url === site("hero.effect_first"));
  const rest = on.filter((f) => f !== first).map(item);
  return { items: first ? [item(first), { url: base }, ...rest] : [{ url: base }, ...rest], slow: !!first, hideText: false };
};

const SOCIAL = {
  facebook: <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />,
  instagram: <><rect x="2" y="2" width="20" height="20" rx="5" stroke="currentColor" fill="none" strokeWidth="2" /><circle cx="12" cy="12" r="4" stroke="currentColor" fill="none" strokeWidth="2" /></>,
  tiktok: <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />,
};

const Decor = () => (
  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.5 }}
    className="absolute bottom-8 right-8 hidden lg:flex flex-col items-center gap-4 z-10">
    {Object.entries(SOCIAL).map(([k, icon]) => (
      <motion.a key={k} href="#" whileHover={{ scale: 1.1, y: -2 }}
        className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center text-gray-400 hover:text-sky-400 hover:border-sky-500 transition-colors">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">{icon}</svg>
      </motion.a>
    ))}
  </motion.div>
);

const useCar = () => {
  const site = useSiteText();
  const cars = useWashCars();
  const raw = site("wash.car");
  const ids = cars.map((c) => c.id).join();
  return useMemo(() => pickCar(raw, cars), [raw, ids]); // eslint-disable-line react-hooks/exhaustive-deps
};

// Layout/zoom settings shared by every hero mode
const useLetters = (src, carId, size, washPortrait = false) => {
  const site = useSiteText();
  const layout = site("hero.layout") || "classic";
  const anim = site("hero.anim") || "none";
  const anchor = readAnchor(site, src, carId);
  const portrait = size.w > 0 && size.h > size.w;
  const map = washPortrait && portrait ? { rect: { left: 0, top: size.h * 0.3, width: size.w, height: size.h * 0.44 }, contain: true } : mapFor(anchor, size.w, size.h, site("hero.car_pos"));
  const door = map && !portrait ? map.box(anchor.door) : null;
  const zoomTo = door ? Math.min(3.2, Math.max(1.6, (size.w * 0.42) / Math.max(door.w * 1.7, size.w * 0.2))) : 1.8;
  const origin = door ? `${door.x + door.w / 2}px ${door.y + door.h / 2}px` : "65% 55%";
  const lay = portrait ? (layout === "contour" && map ? "contour-phone" : "mobile") : NEEDS_CAR.has(layout) && !map ? "classic" : layout;
  return { layout: lay, anim, anchor, map, zoomTo, origin, overlay: site("hero.overlay") };
};

const Shade = ({ layout }) => (
  <div className={`absolute inset-0 pointer-events-none ${layout === "classic" || layout === "split"
    ? "bg-gradient-to-r from-black/60 via-black/30 to-black/10 sm:from-black/85 sm:via-black/45 sm:to-black/10" : "bg-gradient-to-t from-black/80 via-black/20 to-black/40"}`} />
);

const FADE = { WebkitMaskImage: "linear-gradient(to bottom, transparent, #000 14%, #000 86%, transparent)", maskImage: "linear-gradient(to bottom, transparent, #000 14%, #000 86%, transparent)" };

// Still-photo effects (AI-edited copies of the same photo) crossfading in a loop
const FrameCycle = ({ items, secs, box, slow }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (items.length < 2) return undefined;
    const t = setTimeout(() => setI((x) => (x + 1) % items.length), secs * 1000 * (slow && i === 0 ? 2 : 1));
    return () => clearTimeout(t);
  }, [i, items.length, secs, slow]);
  return items.map((f, j) => {
    const p = { initial: { opacity: j === 0 ? 1 : 0 }, animate: { opacity: j === i ? 1 : 0 }, transition: { duration: 1.4, ease: "easeInOut" }, className: box.className, style: box.style, "data-testid": `hero-frame-${j}` };
    return f.video
      ? <motion.video key={f.url} src={f.video} poster={f.url} autoPlay muted loop playsInline preload="auto" disablePictureInPicture {...p} />
      : <motion.img key={f.url} src={f.url} alt="" {...p} />;
  });
};

export const Backdrop = ({ mode, url, car, map, loop = true, frames = [], secs = 4, slow = false }) => {
  const box = map?.rect ? { className: "absolute max-w-none", style: { ...map.rect, ...(map.mask ? { WebkitMaskImage: map.mask, maskImage: map.mask } : map.contain ? FADE : {}) } } : { className: "absolute inset-0 w-full h-full object-cover", style: {} };
  if ((mode === "image" || mode === "car") && frames.length && (frames.length > 1 || frames[0].video || frames[0].url !== (mode === "car" ? car?.clean : url))) return <FrameCycle items={frames} secs={secs} box={box} slow={slow} />;
  if (mode === "video") return <video src={url} autoPlay muted loop={loop} playsInline preload="auto" disablePictureInPicture className={box.className} style={box.style} data-testid="hero-bg-video" />;
  if (mode === "image") return <motion.img src={url} alt="" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2 }} className={box.className} style={box.style} data-testid="hero-bg-image" />;
  if (mode === "car") return <motion.img src={car.clean} alt="" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2 }} className={box.className} style={box.style} data-testid="hero-bg-car" />;
  return <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_40%,rgba(14,165,233,0.12),transparent_60%)]" data-testid="hero-bg-plain" />;
};

// "Drive in" videos: lettering rides in with the car and settles where it parks
export const DriveIn = ({ on, vw, children }) => (on
  ? <motion.div className="absolute inset-0" initial={{ x: -vw * 0.8, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 4.4, ease: [0.15, 0.6, 0.25, 1], opacity: { duration: 1.6 } }} data-testid="hero-drive-in">{children}</motion.div>
  : <div className="absolute inset-0">{children}</div>);

// Photo / silent video / still clean car / plain — with lettering layout and optional scroll animation
const StaticHero = ({ mode, onQuoteClick }) => {
  const site = useSiteText();
  const car = useCar();
  const wrap = useRef(null);
  const box = useRef(null);
  const size = useBox(box);
  const url = site("hero.bg_url");
  const L = useLetters(mode === "car" ? car.clean : url, mode === "car" ? car.id : "", size);
  const tall = L.anim !== "none";
  const { scrollYProgress } = useScroll({ target: wrap, offset: ["start start", "end end"] });
  const p = useSpring(scrollYProgress, WASH_SPRING);
  const scale = useTransform(p, [0, 0.75], [1, L.anim === "zoom" ? L.zoomTo : L.anim === "drift" ? 1.12 : 1]);
  const bgY = useTransform(p, [0, 1], ["0%", L.anim === "drift" ? "-10%" : "0%"]);
  const fade = useTransform(p, [0, 0.7], [1, L.anim === "fade" ? 0 : 1]);
  const rise = useTransform(p, [0, 0.7], [0, L.anim === "fade" ? -120 : 0]);
  const onCar = NEEDS_CAR.has(L.layout);
  const drive = mode === "video" && site("hero.bg_motion") === "drive";
  const plan = effectPlan(site, mode === "car" ? car.clean : url);
  const letters = <HeroLetters layout={L.layout} map={L.map} anchor={L.anchor} vw={size.w} vh={size.h} onQuoteClick={onQuoteClick} />;
  return (
    <div ref={wrap} className={`relative bg-black ${tall ? "h-[220vh]" : ""}`} data-testid="hero-static-wrap">
      <section ref={box} data-testid="hero-section" data-hero-mode={mode} data-hero-layout={L.layout} data-hero-anim={L.anim}
        className="sticky top-0 h-[100svh] min-h-[340px] flex items-center overflow-hidden bg-black">
        <motion.div className="absolute inset-0" style={{ scale, y: bgY, transformOrigin: L.origin, willChange: "transform" }} data-testid="hero-stage">
          {size.w > 0 && <Backdrop mode={mode} url={url} car={car} map={L.map} loop={site("hero.video_loop") === "on"} frames={plan.items} slow={plan.slow} secs={Number(site("hero.effect_secs")) || 4} />}
          {mode !== "plain" && <Shade layout={L.layout} />}
          {L.overlay === "ceramic" && mode === "video" && L.layout !== "mobile" && <CeramicCallout map={L.map} anchor={L.anchor} />}
          {onCar && <DriveIn on={drive} vw={size.w}>{letters}</DriveIn>}
        </motion.div>
        {!onCar && <motion.div className="relative w-full h-full flex items-center" style={{ opacity: fade, y: rise }}>{letters}</motion.div>}
        {(L.layout === "classic" || L.layout === "center") && L.anim !== "zoom" && <Decor />}
      </section>
    </div>
  );
};

const Hero = (props) => {
  const site = useSiteText();
  const loaded = useSiteLoaded();
  const raw = site("hero.mode");
  const mode = raw === "plain" || ((raw === "image" || raw === "video") && site("hero.bg_url")) ? raw : "car"; // wash animation removed → still car
  if (!loaded) return <section data-testid="hero-section" className="h-[100svh] min-h-[340px] bg-black" />;
  return <><RotatePrompt /><StaticHero mode={mode} {...props} /></>;
};

export default Hero;
